import React, { forwardRef, useEffect, useRef } from 'react'
import { classMerge } from '../../utils/classMerge'
import urlVideoById from '../../utils/urlVideoById'
import extractIframe from '../../utils/extractIframe'
import { usePageSpeed } from '../../hooks/usePageSpeed'

import styles from './BackgroundVideo.module.css'
import { Container } from './BackgroundVideo.styles'

export interface BackgroundVideoProps extends React.HTMLAttributes<HTMLIFrameElement> {
  url?: string
  embed?: string
  loop: boolean | undefined
  audio: boolean | undefined
}

export const BackgroundVideo = forwardRef<HTMLIFrameElement, BackgroundVideoProps>(({
  url,
  embed,
  loop,
  audio,
  className,
  ...props
}: BackgroundVideoProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { shouldRenderContent } = usePageSpeed()

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const container = containerRef.current
        const iframe = containerRef.current.querySelector('iframe')

        if (!iframe)
          return;

        const containerWidth = container.offsetWidth
        const containerHeight = container.offsetHeight

        const aspectRatio = 16 / 9

        let iframeWidth = containerWidth
        let iframeHeight = containerWidth / aspectRatio

        if (iframeHeight < containerHeight) {
          iframeHeight = containerHeight
          iframeWidth = containerHeight * aspectRatio
        }

        iframe.style.width = `${iframeWidth}px`
        iframe.style.height = `${iframeHeight}px`
      }
    }

    if (shouldRenderContent) {
      requestAnimationFrame(() => {
        handleResize()
      })
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [shouldRenderContent])

  if (!shouldRenderContent) {
    return (
      <Container
        ref={containerRef} 
        className={`${styles.backgroundVideo}`}
        style={{ backgroundColor: '#000000' }}
        {...props}
      />
    )
  }

  return (
    <Container
      ref={containerRef} 
      className={`${styles.backgroundVideo}`}
      {...props}
    >
      { url &&
        <iframe
          className={classMerge('pointer-events-none', className as string)}
          src={urlVideoById(url, loop, audio)}
          title="Video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        ></iframe>
      }

      { embed &&
        <div dangerouslySetInnerHTML={{__html: extractIframe(embed)}}></div>
      }
    </Container>
  )
})
